import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useTurnstile, TurnstileFallback } from "../hooks/useTurnstile";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

// Batch 2 §12 — Worker frontage via api.jjargent.com (custom domain).
// Set REACT_APP_WORKER_API in frontend/.env; falls back to the custom domain.
const WORKER_API = process.env.REACT_APP_WORKER_API || "https://api.jjargent.com/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Guestbook() {
  useDocumentMeta({
    title: "Guestbook · Say what you're building — JJ Argent",
    description:
      "Leave a note. Say what you're building. A public guestbook for collaborators, recruiters and fellow operators.",
    path: "/guestbook",
    noindex: true, // Batch 2 §10 — unpublished until there's activity
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [posting, setPosting] = useState(false);
  // Quick note (Worker-backed contact)
  const [noteName, setNoteName] = useState("");
  const [noteEmail, setNoteEmail] = useState("");
  const [noteMessage, setNoteMessage] = useState("");
  const [sending, setSending] = useState(false);
  // Turnstile — one instance per form
  const gbTurnstile = useTurnstile();
  const qnTurnstile = useTurnstile();

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${WORKER_API}/guestbook`);
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("guestbook: failed to load", err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const submitEntry = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    if (!gbTurnstile.token) {
      toast.error("Please complete the bot check");
      return;
    }
    setPosting(true);
    try {
      const res = await fetch(`${WORKER_API}/guestbook`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), message: message.trim(), turnstileToken: gbTurnstile.token }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Posted to the guestbook");
        setName("");
        setMessage("");
        gbTurnstile.reset();
        loadEntries();
      } else {
        toast.error(data.error || "Could not post");
      }
    } catch (err) {
      toast.error("Failed to post. Try again.");
    } finally {
      setPosting(false);
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteName.trim() || !noteEmail.trim() || !noteMessage.trim()) return;
    if (!qnTurnstile.token) {
      toast.error("Please complete the bot check");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${WORKER_API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: noteName.trim(),
          email: noteEmail.trim(),
          message: noteMessage.trim(),
          turnstileToken: qnTurnstile.token,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Note sent — JJ will see it");
        setNoteName("");
        setNoteEmail("");
        setNoteMessage("");
        qnTurnstile.reset();
      } else {
        toast.error(data.error || "Could not send");
      }
    } catch (err) {
      toast.error("Failed to send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-[#0B0B0C] text-[#FAF9F6] min-h-screen" data-testid="guestbook-page">
      {/* Hero */}
      <section className="border-b border-[#343437]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-16 lg:pt-32 lg:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3">
                <BookOpen size={16} className="text-[#D8A936]" aria-hidden />
                <p className="eyebrow">Guestbook</p>
              </div>
              <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
                Leave a note.{" "}<br />
                <span className="text-[#D8A936]">Say what you're building.</span>
              </h1>
              <p className="mt-8 text-[#A9A9A6] text-lg leading-relaxed max-w-2xl">
                A friendly wall for readers, colleagues and passers-by. Kept minimal, moderated on
                sight, and read every Monday when I open the weekly pulse.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Guestbook form + entries */}
      <section className="border-b border-[#343437]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="eyebrow">Add your entry</p>
              <h2 className="mt-5 font-display text-3xl md:text-4xl leading-tight">Sign the book</h2>
              <p className="mt-4 text-[#A9A9A6] text-sm leading-relaxed">
                First name is fine. No email required. Keep it kind.
              </p>

              <form onSubmit={submitEntry} className="mt-8 space-y-4" data-testid="guestbook-form">
                <div>
                  <label htmlFor="gb-name" className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">
                    Name
                  </label>
                  <input
                    id="gb-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    required
                    data-testid="guestbook-name"
                    className="mt-2 w-full bg-[#151517] border border-[#343437] px-4 py-3 text-[#FAF9F6] outline-none focus:border-[#D8A936] transition-colors"
                    placeholder="e.g. Alex"
                  />
                </div>
                <div>
                  <label htmlFor="gb-message" className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">
                    Message
                  </label>
                  <textarea
                    id="gb-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={600}
                    required
                    rows={4}
                    data-testid="guestbook-message"
                    className="mt-2 w-full bg-[#151517] border border-[#343437] px-4 py-3 text-[#FAF9F6] outline-none focus:border-[#D8A936] transition-colors resize-none"
                    placeholder="What are you building? What did the case study land on?"
                  />
                  <p className="mt-1 text-[10px] text-[#A9A9A6] text-right">{message.length}/600</p>
                </div>
                <button
                  type="submit"
                  disabled={posting}
                  data-testid="guestbook-submit"
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {posting ? "Posting…" : "Sign the guestbook"}
                </button>
                <div ref={gbTurnstile.containerRef} data-testid="guestbook-turnstile" className="mt-2" />
                <TurnstileFallback show={gbTurnstile.scriptFailed} />
              </form>
            </div>

            <div className="lg:col-span-7">
              <p className="eyebrow">Recent entries</p>
              <h2 className="mt-5 font-display text-3xl md:text-4xl leading-tight">
                <span className="text-[#D8A936]">{entries.length}</span> {entries.length === 1 ? "note" : "notes"} so far
              </h2>

              <div className="mt-8 space-y-4" data-testid="guestbook-entries">
                {loading && (
                  <p className="text-sm text-[#A9A9A6]" data-testid="guestbook-loading">Loading…</p>
                )}
                {!loading && entries.length === 0 && (
                  <p className="text-sm text-[#A9A9A6]" data-testid="guestbook-empty">
                    Nothing here yet. Be the first to sign.
                  </p>
                )}
                {!loading && entries.map((entry, i) => (
                  <motion.article
                    key={entry.id || `${entry.name}-${entry.posted_at || i}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.4) }}
                    className="border border-[#343437] bg-[#151517]/60 p-5 hover:border-[#D8A936] transition-colors"
                    data-testid={`guestbook-entry-${i}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="font-display text-[15px] text-[#FAF9F6]">{entry.name}</p>
                      <p className="text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6]">
                        {formatDate(entry.posted_at || entry.created_at)}
                      </p>
                    </div>
                    <p className="mt-3 text-[15px] text-[#FAF9F6] leading-relaxed whitespace-pre-wrap">
                      {entry.message}
                    </p>
                  </motion.article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick note — Worker-backed contact snippet */}
      <section className="bg-[#0B0B0C]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3">
                <MessageCircle size={16} className="text-[#D8A936]" aria-hidden />
                <p className="eyebrow">Quick note</p>
              </div>
              <h2 className="mt-5 font-display text-3xl md:text-4xl leading-tight">
                A shorter way to reach me
              </h2>
              <p className="mt-4 text-[#A9A9A6] leading-relaxed max-w-md">
                Not ready for the full enquiry form? Drop a couple of sentences here. For anything with
                scope, budget or a role attached, the{" "}
                <a href="/contact" className="text-[#D8A936] hover:text-[#FAF9F6] underline underline-offset-4">
                  full contact form
                </a>{" "}
                is the better path.
              </p>
            </div>

            <div className="lg:col-span-7">
              <form onSubmit={submitNote} className="space-y-4 border border-[#343437] bg-[#151517] p-6 lg:p-8" data-testid="quick-note-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="qn-name" className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">
                      Name
                    </label>
                    <input
                      id="qn-name"
                      type="text"
                      value={noteName}
                      onChange={(e) => setNoteName(e.target.value)}
                      required
                      maxLength={120}
                      data-testid="quick-note-name"
                      className="mt-2 w-full bg-[#0B0B0C] border border-[#343437] px-4 py-3 text-[#FAF9F6] outline-none focus:border-[#D8A936] transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="qn-email" className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">
                      Email
                    </label>
                    <input
                      id="qn-email"
                      type="email"
                      value={noteEmail}
                      onChange={(e) => setNoteEmail(e.target.value)}
                      required
                      maxLength={200}
                      data-testid="quick-note-email"
                      className="mt-2 w-full bg-[#0B0B0C] border border-[#343437] px-4 py-3 text-[#FAF9F6] outline-none focus:border-[#D8A936] transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="qn-message" className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">
                    Message
                  </label>
                  <textarea
                    id="qn-message"
                    value={noteMessage}
                    onChange={(e) => setNoteMessage(e.target.value)}
                    required
                    maxLength={1000}
                    rows={4}
                    data-testid="quick-note-message"
                    className="mt-2 w-full bg-[#0B0B0C] border border-[#343437] px-4 py-3 text-[#FAF9F6] outline-none focus:border-[#D8A936] transition-colors resize-none"
                  />
                  <p className="mt-1 text-[10px] text-[#A9A9A6] text-right">{noteMessage.length}/1000</p>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="quick-note-submit"
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {sending ? "Sending…" : "Send note"}
                </button>
                <div ref={qnTurnstile.containerRef} data-testid="quick-note-turnstile" className="mt-2" />
                <TurnstileFallback show={qnTurnstile.scriptFailed} />
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Batch 2 §10.2 — public unauthenticated file upload removed.
          Was an abuse vector (any file, any user, hosted on jjargent.com).
          If reinstated later it must be behind auth + type allow-list + size
          limits + rate limits + AV scanning. Also removed the leaked internal
          "ANY FILE · UP TO YOUR WORKER'S LIMIT" developer copy (§10.3). */}
    </main>
  );
}
